import { useEffect, useReducer } from "react";
import { useForm } from "@tanstack/react-form";
import { Schema } from "effect";
import type { PatientInfoPolicyView } from "@ascleo/frontdesk-contracts/patient-info/policy";
import { patientInfoRpcClient } from "../../lib/rpc/patient-info.client";
import { reportOperationalError } from "../../lib/operational-error-telemetry";
import { usePlatform } from "../../platform/context";

const PatientInfoSettingsForm = Schema.Struct({
  enabled: Schema.Boolean,
  phone: Schema.Boolean,
  email: Schema.Boolean,
  hausarzt: Schema.Boolean,
});

/**
 * Draft values of the practice-wide patient-information review policy.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * const draft: PatientInfoPolicyDraft = { enabled: true, phone: true, email: false, hausarzt: true };
 * ```
 */
export type PatientInfoPolicyDraft = typeof PatientInfoSettingsForm.Type;

const DEFAULT_VALUES: PatientInfoPolicyDraft = {
  enabled: false,
  phone: true,
  email: true,
  hausarzt: true,
};

type LoadStatus = "loading" | "ready" | "unavailable" | "error";
type SaveStatus = "idle" | "saving" | "saved" | "conflict" | "error";

/**
 * Load and save state of the patient-information policy, as far as the
 * settings page shows it.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * const state: PatientInfoPolicyUiState = { loadStatus: "loading", saveStatus: "idle" };
 * ```
 */
export interface PatientInfoPolicyUiState {
  readonly server?: PatientInfoPolicyView;
  readonly loadStatus: LoadStatus;
  readonly saveStatus: SaveStatus;
}

type PolicyUiAction =
  | { readonly type: "loaded"; readonly server: PatientInfoPolicyView }
  | { readonly type: "load-failed" }
  | { readonly type: "save-started" }
  | { readonly type: "saved"; readonly server: PatientInfoPolicyView }
  | { readonly type: "conflict"; readonly server: PatientInfoPolicyView }
  | { readonly type: "save-failed" };

const INITIAL_STATE: PatientInfoPolicyUiState = {
  loadStatus: "loading",
  saveStatus: "idle",
};

function policyUiReducer(
  state: PatientInfoPolicyUiState,
  action: PolicyUiAction,
): PatientInfoPolicyUiState {
  switch (action.type) {
    case "loaded":
      return {
        server: action.server,
        loadStatus:
          action.server.availability === "available" ? "ready" : "unavailable",
        saveStatus: "idle",
      };
    case "load-failed":
      return { loadStatus: "error", saveStatus: "idle" };
    case "save-started":
      return { ...state, saveStatus: "saving" };
    case "saved":
      return {
        server: action.server,
        loadStatus: "ready",
        saveStatus: "saved",
      };
    case "conflict":
      return {
        server: action.server,
        loadStatus: "ready",
        saveStatus: "conflict",
      };
    case "save-failed":
      return { ...state, saveStatus: "error" };
  }
}

function valuesOf(policy: PatientInfoPolicyView): PatientInfoPolicyDraft {
  return {
    enabled: policy.enabled,
    phone: policy.fields.phone,
    email: policy.fields.email,
    hausarzt: policy.fields.hausarzt,
  };
}

/**
 * Writes server truth into the mounted switches and makes it the new clean
 * baseline, so the page's save action is disabled again afterwards.
 */
function applyServerValues(
  formApi: {
    setFieldValue: (
      name: keyof PatientInfoPolicyDraft,
      value: boolean,
    ) => void;
    reset: (values: PatientInfoPolicyDraft) => void;
  },
  values: PatientInfoPolicyDraft,
): void {
  formApi.setFieldValue("enabled", values.enabled);
  formApi.setFieldValue("phone", values.phone);
  formApi.setFieldValue("email", values.email);
  formApi.setFieldValue("hausarzt", values.hausarzt);
  formApi.reset(values);
}

/**
 * True when a draft would switch the review on with no category to review —
 * the one draft the page refuses to save.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * patientInfoPolicyLacksSelection({ enabled: true, phone: false, email: false, hausarzt: false }); // true
 * ```
 */
export function patientInfoPolicyLacksSelection(
  draft: PatientInfoPolicyDraft,
): boolean {
  return draft.enabled && !(draft.phone || draft.email || draft.hausarzt);
}

/**
 * Owns the practice-wide patient-information review policy for the settings
 * page: loads it from the Bridge policy store once, keeps the draft while the
 * operator moves between sections, and saves it with optimistic versioning
 * when the page's single save action runs. The values are never copied into
 * host-local practice settings; the Bridge stays the enforcement boundary.
 *
 * @since 1.3.0
 * @example
 * ```tsx
 * const patientInfoPolicy = usePatientInfoPolicy();
 * <PatientInfoSettingsSection policy={patientInfoPolicy} />
 * ```
 */
export function usePatientInfoPolicy() {
  const platform = usePlatform();
  const [uiState, dispatch] = useReducer(policyUiReducer, INITIAL_STATE);

  const form = useForm({
    // The loaded server policy is the form's baseline. `useForm` re-applies
    // `defaultValues` on every render of the owning page while the form is
    // untouched, so a constant default here would overwrite the loaded policy
    // as soon as the page re-renders (e.g. on section navigation).
    defaultValues:
      uiState.server === undefined ? DEFAULT_VALUES : valuesOf(uiState.server),
    validators: {
      onChange: Schema.toStandardSchemaV1(PatientInfoSettingsForm),
    },
    onSubmit: async ({ value, formApi }) => {
      const server = uiState.server;
      if (server === undefined || server.availability !== "available") return;
      if (patientInfoPolicyLacksSelection(value)) return;
      dispatch({ type: "save-started" });
      try {
        const result = await patientInfoRpcClient.policy.save({
          enabled: value.enabled,
          fields: {
            phone: value.phone,
            email: value.email,
            hausarzt: value.hausarzt,
          },
          expectedVersion: server.version,
        });
        if (result.outcome === "unavailable") {
          dispatch({ type: "save-failed" });
          return;
        }
        if (result.outcome === "version-conflict") {
          applyServerValues(formApi, valuesOf(result.current));
          dispatch({ type: "conflict", server: result.current });
          return;
        }
        applyServerValues(formApi, valuesOf(result.view));
        dispatch({ type: "saved", server: result.view });
      } catch (error) {
        dispatch({ type: "save-failed" });
        void reportOperationalError({
          platform,
          action: "patient-info.policy-save",
          severity: "error",
          source: "rpc",
          transport: "effect-rpc-http",
          component: "PatientInfoSettingsSection",
          error,
        });
      }
    },
  });

  useEffect(
    function loadPatientInfoPolicy() {
      let active = true;
      void patientInfoRpcClient.policy
        .read()
        .then((server) => {
          if (active) dispatch({ type: "loaded", server });
          return server;
        })
        .catch((error: unknown) => {
          if (!active) return;
          dispatch({ type: "load-failed" });
          void reportOperationalError({
            platform,
            action: "patient-info.policy-load",
            severity: "error",
            source: "rpc",
            transport: "effect-rpc-http",
            component: "PatientInfoSettingsSection",
            error,
          });
        });
      return function stopApplyingPolicyResponses() {
        active = false;
      };
    },
    [platform],
  );

  useEffect(
    function synchronizeMountedPolicyFields() {
      if (uiState.server === undefined) return;
      form.reset(valuesOf(uiState.server));
    },
    [form, uiState.server],
  );

  return { form, uiState };
}

/**
 * The patient-information policy as the settings page consumes it.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * type Policy = PatientInfoPolicyController;
 * ```
 */
export type PatientInfoPolicyController = ReturnType<
  typeof usePatientInfoPolicy
>;
